'use client';

import React from 'react';
import { Sparkles, Layout, Loader2 } from 'lucide-react';

interface PageLoaderProps {
  type?: 'board' | 'grid' | 'full';
  title?: string;
}

export default function PageLoader({ type = 'board', title = 'Loading ScaleSutra...' }: PageLoaderProps) {
  if (type === 'full') {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex flex-col items-center justify-center p-6 text-center">
        <div className="relative flex items-center justify-center mb-6">
          <div className="absolute w-20 h-20 rounded-3xl bg-indigo-600/20 blur-xl animate-pulse" />
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-purple-500 flex items-center justify-center shadow-xl shadow-indigo-600/30 border border-indigo-400/30">
            <Layout className="w-8 h-8 text-white animate-bounce" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          {title}
        </h2>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-6">
          <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-spin" /> Synchronizing Workspace
        </div>
        <div className="w-48 h-1.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800">
          <div className="h-full w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-shimmer" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col space-y-4">
      {/* Top Bar Skeleton */}
      <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-slate-900 border border-slate-800 animate-pulse" />
          <div className="space-y-2">
            <div className="w-36 h-4 rounded-lg bg-slate-800 animate-shimmer" />
            <div className="w-20 h-2.5 rounded bg-slate-900 animate-pulse" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-32 h-8 rounded-xl bg-slate-900 border border-slate-800 animate-pulse" />
          <div className="w-24 h-8 rounded-xl bg-indigo-600/20 border border-indigo-500/30 animate-pulse" />
        </div>
      </div>

      {/* Grid or Kanban Columns Skeleton */}
      {type === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 p-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-slate-950/80 border border-slate-800/90 rounded-2xl p-5 space-y-4 shadow-lg animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20" />
                <div className="w-14 h-4 rounded-full bg-slate-900" />
              </div>
              <div className="space-y-2">
                <div className="w-3/4 h-5 rounded-lg bg-slate-800 animate-shimmer" />
                <div className="w-full h-3.5 rounded bg-slate-900" />
                <div className="w-2/3 h-3.5 rounded bg-slate-900" />
              </div>
              <div className="pt-3 border-t border-slate-900 flex items-center justify-between">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-950" />
                  <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-950" />
                </div>
                <div className="w-16 h-3 rounded bg-slate-900" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-row items-start gap-4 sm:gap-5 overflow-x-auto pb-6">
          {[1, 2, 3, 4].map((col) => (
            <div
              key={col}
              className="w-[280px] sm:w-[320px] shrink-0 bg-slate-950/90 border border-slate-800/90 rounded-2xl p-4 space-y-3 shadow-xl"
            >
              {/* Column Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-ping" />
                  <div className="w-28 h-4 rounded-md bg-slate-800 animate-shimmer" />
                </div>
                <div className="w-6 h-5 rounded-full bg-slate-900 text-center text-xs text-slate-600 font-mono">
                  ••
                </div>
              </div>

              {/* Column Task Cards */}
              {[1, 2, 3].map((card) => (
                <div
                  key={card}
                  className="bg-slate-900/60 border border-slate-800/80 rounded-xl p-3.5 space-y-3 animate-pulse"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-3.5 rounded-full bg-indigo-500/20 border border-indigo-500/30" />
                    <div className="w-12 h-3.5 rounded bg-slate-800" />
                  </div>
                  <div className="w-full h-4 rounded-md bg-slate-800 animate-shimmer" />
                  <div className="w-4/5 h-3 rounded bg-slate-950" />

                  <div className="pt-2 border-t border-slate-800/60 flex items-center justify-between">
                    <div className="w-6 h-6 rounded-full bg-slate-800" />
                    <div className="w-14 h-3 rounded bg-slate-950" />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
